import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { calendarEventListTag } from "calendar/_api/calendar-event-api";
import type { RootState } from "_shared/_stores/root-reducer";
import type { AppDispatch } from "_shared/_stores/store-types";
import EntityMember from "_shared/entity-members/entity-member";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Session from "users/__session/session";
import { ProposalContext } from "work/proposals/__constants/proposal-lifecycle";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuery from "work/work-agreements/_api/queries/work-agreement-query";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { WorkAgreementJSON, WorkAgreementJSONZ } from "work/work-agreements/_schemas/work-agreement-schema";
import VendorTeamSchema, { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import WorkAgreement from "work/work-agreements/work-agreement";
import z from "zod";

type WorkAgreementApiCacheContext = {
  dispatch: AppDispatch;
  getState: () => RootState;
  session: Session;
};

type WorkAgreementUpsertCacheContext = WorkAgreementApiCacheContext & {
  previousWorkAgreementJSON?: WorkAgreementJSON | null;
  workAgreementJSON: WorkAgreementJSON;
};

type WorkAgreementRemoveCacheContext = WorkAgreementApiCacheContext & {
  previousWorkAgreementJSON?: WorkAgreementJSON | null;
  workAgreementIdValue: string;
};

const invalidateWorkAgreementListApiCaches = (dispatch: AppDispatch) => {
  dispatch(
    workAgreementsApi.util.invalidateTags([
      { type: "WorkAgreement", id: "LIST" },
      { type: "WorkAgreement", id: "ActiveCount" },
      { type: "WorkAgreement", id: "ArchiveCount" },
    ]),
  );
};

export const upsertWorkAgreementApiCaches = ({
  dispatch,
  session,
  workAgreementJSON,
}: WorkAgreementUpsertCacheContext) => {
  if (!workAgreementJSON.id) {
    throw new Error("Cannot cache work agreement without an id.");
  }

  dispatch(
    workAgreementsApi.util.upsertQueryEntries([
      {
        endpointName: "getWorkAgreementById",
        arg: { workAgreementId: workAgreementJSON.id, session },
        value: workAgreementJSON,
      },
    ]),
  );
  invalidateWorkAgreementListApiCaches(dispatch);
};

export const removeWorkAgreementApiCaches = ({
  dispatch,
  session,
  workAgreementIdValue,
}: WorkAgreementRemoveCacheContext) => {
  dispatch(
    workAgreementsApi.util.upsertQueryEntries([
      {
        endpointName: "getWorkAgreementById",
        arg: { workAgreementId: workAgreementIdValue, session },
        value: null,
      },
    ]),
  );
  invalidateWorkAgreementListApiCaches(dispatch);
};

export const workAgreementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getWorkAgreements: build.query<Paginated<WorkAgreementJSON>, { query: WorkAgreementQuery; interfaceName: string }>({
      query: ({ query }) => ({
        url: "work",
        params: query?.searchParams,
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data: workAgreementsResponse } = await queryFulfilled;
          const session = Session.loadFromStorage(() => {});
          const entries = workAgreementsResponse.data
            .filter((workAgreementJSON): workAgreementJSON is WorkAgreementJSON & { id: string } =>
              Boolean(workAgreementJSON.id)
            )
            .map((workAgreementJSON) => ({
              endpointName: "getWorkAgreementById" as const,
              arg: { workAgreementId: workAgreementJSON.id, session },
              value: workAgreementJSON,
            }));
          if (entries.length > 0) {
            dispatch(workAgreementsApi.util.upsertQueryEntries(entries));
          }
        } catch {
          // Failed list requests have no rows to hydrate into the per-work-agreement cache.
        }
      },
      transformResponse: (response: WorkAgreementJSON[], meta): Paginated<WorkAgreementJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((item) => ({
                type: "WorkAgreement" as const,
                id: item.id ?? undefined,
              })),
              { type: "WorkAgreement" as const, id: "LIST" },
            ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkAgreementById: build.query<WorkAgreementJSON | null, { workAgreementId: string; session: Session }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}`,
      rawResponseSchema: WorkAgreementJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [{ type: "WorkAgreement", id: request.workAgreementId }],
    }),
    getWorkAgreementsByIds: build.query<BarebonesWorkAgreementResponse[], { workAgreementIds: string[] }>({
      query: ({ workAgreementIds }) => ({
        url: "work/get-by-ids",
        method: "POST",
        body: workAgreementIds,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({
                type: "WorkAgreement" as const,
                id: item.id ?? undefined,
              })),
              { type: "WorkAgreement" as const, id: "LIST" },
            ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkTeamsByWorkAgreementId: build.query<VendorTeamJSON[], { workAgreementId: string }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}/teams`,
      rawResponseSchema: z.array(VendorTeamSchema.VendorTeamJSONZ),
      onSchemaFailure: logSchemaFailure,
    }),
    // #endregion

    // #region Counts
    /**
     * The most recently started active agreements, in the same context the active count is taken in.
     *
     * Deliberately not built on `WorkAgreementQuery`: that object carries no `context`, so a list
     * built from it answers a different question from `getActiveWorkAgreementsCount` and a card
     * showing both would put a total next to rows that are not drawn from it. The parameters here
     * are the count's parameters with a page asked for instead of a page size of zero.
     */
    getActiveWorkAgreements: build.query<Paginated<WorkAgreementJSON>, { session: Session; pageSize: number }>({
      query: ({ session, pageSize }) => ({
        url: "work",
        params: new URLSearchParams({
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          orderBy: WorkAgreementOrderField.Start,
          orderDir: OrderDirection.Descending,
          pageIndex: "0",
          pageSize: String(pageSize),
        }),
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: WorkAgreementJSON[], meta): Paginated<WorkAgreementJSON> => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
      providesTags: [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getActiveWorkAgreementsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work",
        params: new URLSearchParams({
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ActiveCount" }],
    }),
    getArchivedWorkAgreementsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work",
        params: new URLSearchParams({
          isArchived: "true",
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ArchiveCount" }],
    }),
    // #endregion

    // #region End Work Agreement
    endWorkAgreement: build.mutation<WorkAgreementJSON, { workAgreementId: string }>({
      query: ({ workAgreementId }) => ({
        url: `work/${workAgreementId}/end-agreement`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: WorkAgreementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const session = Session.loadFromStorage(() => {});
        try {
          const { data: workAgreementJSON } = await queryFulfilled;
          upsertWorkAgreementApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            session,
            workAgreementJSON,
          });
        } catch {
          // Failed mutations have no work agreement data to hydrate.
        }
      },
      invalidatesTags: [calendarEventListTag],
    }),
    // #endregion

    // #region Revise Work Agreement
    reviseWorkAgreement: build.mutation<ProposalJSON, { workAgreement: WorkAgreement; session: Session }>({
      query: ({ workAgreement, session }) => {
        if (!session.user) {
          throw new Error("Cannot revise work agreement without a user.");
        }
        if (!workAgreement.id) {
          throw new Error("Cannot revise work agreement without id.");
        }
        return {
          url: `work/${workAgreement.id.value}/revise`,
          method: "POST",
          body: workAgreement.id.value,
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.workAgreement.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Quit Team
    quitWorkTeam: build.mutation<ProposalJSON | null, { workId: string }>({
      query: ({ workId }) => ({
        url: `work/${workId}/teams/quit`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const session = Session.loadFromStorage(() => {});
        try {
          await queryFulfilled;
          removeWorkAgreementApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            session,
            workAgreementIdValue: args.workId,
          });
        } catch {
          // Failed mutations should leave the local work-agreement caches untouched.
        }
      },
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Replace Vendor Team Members
    /**
     * Re-staffs a hired vendor team, in full: the roster sent is the roster the team ends up with,
     * so a member left out of it is a member removed.
     *
     * Answers in one of two shapes, and the caller has to handle both. Nothing comes back — a `204`,
     * which `fetchBaseQuery` hands on as `null` — when the roster simply changed on the live
     * agreement. A proposal comes back when the hired roster was a term of the agreement
     * (`WorkAgreement.vendorTeamRosterIsAnAgreedTerm`) and a revision was opened instead, which is
     * the same answer `work/{id}/teams/quit` gives for the same reason, and the caller must take
     * the user to it rather than report a quiet success.
     *
     * The leader is not part of the roster. Who speaks for the vendor is the party's standing on
     * the agreement, and moving it is a change to the terms — `revise` is the route for that.
     */
    replaceVendorTeamMembers: build.mutation<
      ProposalJSON | null,
      { workAgreementId: string; members: EntityMember[] }
    >({
      query: ({ workAgreementId, members }) => ({
        url: `work/${workAgreementId}/vendor-team-members`,
        method: "PUT",
        body: {
          members: members.map((member) => ({
            userId: member.userId.value,
            entityId: member.legalEntityId.value,
          })),
        },
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      // The agreement itself is what the view reads the roster from, so its own tag is the one that
      // has to go: re-fetching it is what puts the new roster on the page. The proposal list goes
      // too because the other answer to this command is a revision that now belongs on it.
      invalidatesTags: (_result, _error, args) => [
        { type: "WorkAgreement", id: args.workAgreementId },
        { type: "Proposal", id: args.workAgreementId },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetWorkAgreementsQuery,
  useGetWorkAgreementByIdQuery,
  useGetWorkAgreementsByIdsQuery,
  useGetWorkTeamsByWorkAgreementIdQuery,
  useGetActiveWorkAgreementsQuery,
  useGetActiveWorkAgreementsCountQuery,
  useGetArchivedWorkAgreementsCountQuery,
  useEndWorkAgreementMutation,
  useReviseWorkAgreementMutation,
  useQuitWorkTeamMutation,
  useReplaceVendorTeamMembersMutation,
} = workAgreementsApi;
